import { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { Button, ButtonLabel, CountrySelect, Header } from '@components/Generic'
import type { SelectOption } from '@components/Generic/SelectInput/SelectInput'
import {
  GocardlessLoadErrorScreen,
  GocardlessLoadingScreen,
  GocardlessRedirectingScreen,
} from '@components/Gocardless/GocardlessScreens'
import { StoreContext } from '@store/Root.store'
import { devLog } from '@methods/devLog'
import { getUrlParam, removeUrlParam, setUrlParam } from '@methods/urlParams'
import { getLocalizedCountryLabel } from '@methods/localizeCountries'
import { redirectBackFromGocardless } from '@methods/gocardless'
import {
  IBAN_COUNTRY_URL_PARAM,
  IBAN_REFUSED_URL_PARAM,
  abortIbanSupplement,
  errorCode,
  getIbanSupplementContext,
  leaveWhenNothingToAsk,
  listGocardlessCountries,
  previewIban,
  wasRefused,
} from '@methods/gocardlessIbanSupplement'
import { cx } from '@/utils/cx'
import {
  IBAN_COUNTRY_ERRORS,
  type GocardlessCountry,
  type IbanSupplementErrorCode,
} from '@/types/gocardlessIbanSupplement'

// `/gocardless-iban-confirmation`, step 0: the bank returned an account number but no IBAN, so the
// user names the country their IBAN starts with (it may differ from where they live: a Revolut IBAN
// starts with LT). The page is reached with the `gocardless-session` cookie set, from the return page
// (POST complete answered `ibanSupplementRequired`) or straight from the backend's redirecting GET
// complete, so like the income confirmation screen it skips the basic context and the keep-alive.
//
// Continue asks the backend for the IBAN the country gives (`preview`, nothing stored) and goes on to
// the confirmation step with the country in the URL; Cancel fails the session (`abort`) and hands it
// back to onboarding.

type Status = 'loading' | 'loadError' | 'select' | 'redirecting'
type Pending = 'continue' | 'cancel' | null
// a code: the account number does not fit the country, choose another; `retryable`: the request
// failed, the same answer may succeed; `final`: the backend refused the request (a 400 or 404, see
// `wasRefused`) while the session still waits for it - no country can answer (no account number)
// and the only way out is to hand the session back
type PreviewError = IbanSupplementErrorCode | 'retryable' | 'final' | null

interface CountryOption extends SelectOption {
  icon: string
}

const isCountryError = (
  code: IbanSupplementErrorCode | undefined
): code is IbanSupplementErrorCode => code !== undefined && IBAN_COUNTRY_ERRORS.has(code)

export const GocardlessIbanCountryPage = observer(() => {
  const store = useContext(StoreContext)
  const { gocardlessIbanSupplementPage: trans } = store.TranslationsState.translations
  const { language } = store.TranslationsState

  const [status, setStatus] = useState<Status>('loading')
  const [countries, setCountries] = useState<CountryOption[]>([])
  const [country, setCountry] = useState<CountryOption | null>(null)
  const [pending, setPending] = useState<Pending>(null)
  const [previewError, setPreviewError] = useState<PreviewError>(null)
  // Cancel (or the way out of the dead end) failed with the network or a 5xx: worth another try
  const [abortFailed, setAbortFailed] = useState(false)

  const redirectBack = () => {
    setStatus('redirecting')
    redirectBackFromGocardless()
  }

  const toOption = (item: GocardlessCountry): CountryOption => ({
    value: item.code,
    label: `${item.code} - ${getLocalizedCountryLabel(item.code, item.countryNameEN, language)}`,
    icon: item.countryIcon,
  })

  // the confirmation step sent the user back here because the backend refused the IBAN of the
  // country in the URL: say so, once (the param is dropped so that a reload starts clean)
  const showRefusal = () => {
    const refused = getUrlParam(IBAN_REFUSED_URL_PARAM) as IbanSupplementErrorCode | null
    if (!refused) return

    removeUrlParam(IBAN_REFUSED_URL_PARAM)
    if (refused === 'ibanMismatch' || isCountryError(refused)) {
      setPreviewError(refused)
    }
  }

  const load = async () => {
    setStatus('loading')

    try {
      const context = await getIbanSupplementContext()

      if (leaveWhenNothingToAsk(context)) {
        setStatus('redirecting')
        return
      }

      const options = (await listGocardlessCountries()).map(toOption)
      setCountries(options)

      // a country chosen before (Back from the confirmation step) wins over the bank's
      const preselected = getUrlParam(IBAN_COUNTRY_URL_PARAM) ?? context?.suggestedCountryCode
      setCountry(options.find((option) => option.value === preselected) ?? null)
      showRefusal()
      setStatus('select')
    } catch (e) {
      devLog('Error loading the IBAN country screen:', e)
      setStatus('loadError')
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const handleCountry = (value: SelectOption | null) => {
    setCountry(value as CountryOption | null)
    setPreviewError(null)
    setAbortFailed(false)
  }

  // After a refused request: the context says where a session that moved on goes. Returns whether
  // the screen was left. A failed lookup is not "nothing to ask": the user stays and may retry.
  const leaveWhenSessionMovedOn = async (): Promise<boolean> => {
    let context
    try {
      context = await getIbanSupplementContext()
    } catch (e) {
      devLog('Error reading the IBAN context:', e)
      return false
    }

    if (leaveWhenNothingToAsk(context)) {
      setStatus('redirecting')
      return true
    }
    return false
  }

  const handleContinue = async () => {
    if (pending || !country) return

    // the selector is locked while the preview runs, so the answer is about this country
    const selected = country
    setPending('continue')
    setPreviewError(null)
    setAbortFailed(false)

    try {
      await previewIban(selected.value)
      setUrlParam(IBAN_COUNTRY_URL_PARAM, selected.value)
      store.ScenarioState.nextPage()
    } catch (e) {
      devLog('Error building the IBAN:', e)

      if (wasRefused(e)) {
        const reason = errorCode(e)
        if (isCountryError(reason)) {
          setPreviewError(reason)
        } else if (await leaveWhenSessionMovedOn()) {
          // the session is no longer waiting for the answer: the context said where to go
          return
        } else {
          // still waiting, yet no country can answer (the session has no account number, a request
          // the backend cannot read): a retry would fail the same way
          setPreviewError('final')
        }
      } else {
        setPreviewError('retryable')
      }
    }

    setPending(null)
  }

  // Cancel, and the way out of a dead end: the session fails for want of an IBAN and is handed back.
  const handleCancel = async () => {
    if (pending) return

    setPending('cancel')
    setAbortFailed(false)
    // the dead end stays explained; any other message is about the request that just ran
    if (previewError !== 'final') setPreviewError(null)

    try {
      await abortIbanSupplement()
    } catch (e) {
      devLog('Error aborting the IBAN supplement:', e)

      // a session that already ended is handed back all the same: the backend decides where it goes
      if (wasRefused(e)) {
        redirectBack()
        return
      }

      // anything else (the network, a 5xx): the session still waits, the user can try again
      setAbortFailed(true)
      setPending(null)
      return
    }

    redirectBack()
  }

  if (status === 'loading') {
    return <GocardlessLoadingScreen className="w-500 p-40" />
  }

  if (status === 'redirecting') {
    return (
      <GocardlessRedirectingScreen
        className="w-500 p-40"
        header={trans?.redirectingHeader}
        label={trans?.redirectingLabel}
        buttonLabel={trans?.pleaseWait}
      />
    )
  }

  if (status === 'loadError') {
    return (
      <GocardlessLoadErrorScreen
        className="w-500 p-40"
        header={trans?.countryHeader}
        message={trans?.loadError}
        retryLabel={trans?.tryAgain}
        retryTestId="iban-country-retry"
        onRetry={() => void load()}
      />
    )
  }

  const isFinal = previewError === 'final'
  const previewErrorText = isFinal
    ? trans?.deadEnd
    : previewError === 'retryable'
      ? trans?.submitError
      : previewError
        ? trans?.notValidForCountry
        : null

  return (
    <div className="page-container mobile-m-0 w-500 p-40">
      <Header className="no-margin">{trans?.countryHeader}</Header>

      <p className="generic__paragraph">{trans?.countryDescription}</p>

      <div className="generic__select-holder">
        <label className="generic__label" htmlFor="iban-country-input">
          {trans?.selectLabel}
        </label>
        <CountrySelect
          inputId="iban-country-input"
          value={country}
          onChange={handleCountry}
          externalList={countries}
          showCheckmark
          placeholder={trans?.placeholder}
          isDisabled={pending !== null || isFinal}
        />
      </div>

      {previewErrorText && (
        <p
          className="generic__paragraph generic__error-message"
          role="alert"
          data-testid="iban-country-error"
        >
          {previewErrorText}
        </p>
      )}

      {abortFailed && (
        <p className="generic__paragraph generic__error-message" role="alert">
          {trans?.submitError}
        </p>
      )}

      <div
        className={cx(
          'layout__content-container-bottom-fixed',
          'layout__content-container-bottom-fixed--no-border',
          'layout__content-container-bottom-fixed--stacked'
        )}
      >
        {isFinal ? (
          <Button
            className="button--w164"
            onClick={() => void handleCancel()}
            disabled={pending !== null}
            dataTestid="iban-country-back"
          >
            <ButtonLabel isLoading={pending === 'cancel'} label={trans?.backToOnboarding} />
          </Button>
        ) : (
          <>
            <Button
              className="button--link"
              onClick={() => void handleCancel()}
              disabled={pending !== null}
              dataTestid="iban-country-cancel"
            >
              <ButtonLabel isLoading={pending === 'cancel'} label={trans?.cancel} />
            </Button>

            <Button
              onClick={() => void handleContinue()}
              disabled={pending !== null || !country}
              dataTestid="iban-country-continue"
            >
              <ButtonLabel isLoading={pending === 'continue'} label={trans?.continue} />
            </Button>
          </>
        )}
      </div>
      <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
    </div>
  )
})
